import type { LatLng, PlaceSuggestion } from '../types';

/**
 * Photon geocoding service (komoot).
 * Free, keyless, and suitable for search-as-you-type autocomplete.
 */
export async function searchPlaces(
  query: string,
  options?: { limit?: number; bias?: LatLng; countrycodes?: string },
): Promise<PlaceSuggestion[]> {
  const q = query.trim();
  if (q.length < 2) return [];

  const limit = options?.limit ?? 6;
  const url = new URL('https://photon.komoot.io/api/');
  url.searchParams.set('q', q);
  url.searchParams.set('limit', String(limit));

  if (options?.bias) {
    url.searchParams.set('lat', String(options.bias.latitude));
    url.searchParams.set('lon', String(options.bias.longitude));
  }

  try {
    const res = await fetch(url.toString());
    if (!res.ok) return [];
    const data = await res.json();
    if (!data || !Array.isArray(data.features)) return [];

    return data.features.map((f: any, idx: number): PlaceSuggestion => {
      const p = f.properties || {};
      const [lon, lat] = f.geometry?.coordinates || [0, 0];

      const parts: string[] = [];
      const name = p.name || p.street || p.city || p.county || 'Place';
      if (name) parts.push(name);
      if (p.city && p.city !== name) parts.push(p.city);
      if (p.state) parts.push(p.state);
      if (p.country) parts.push(p.country);

      const subParts: string[] = [];
      if (p.city && p.city !== name) subParts.push(p.city);
      if (p.state) subParts.push(p.state);
      if (p.country) subParts.push(p.country);

      return {
        id: `photon-${p.osm_id ?? idx}-${p.osm_type ?? 'p'}`,
        displayName: parts.join(', '),
        shortName: name,
        subName: subParts.join(', '),
        coordinate: { latitude: lat, longitude: lon },
      };
    });
  } catch {
    return [];
  }
}

// In-memory cache for reverse geocode results
const reverseGeocodeCache = new Map<string, string>();

// Simple throttle queue for Photon reverse geocoding requests
let lastPhotonCallTime = 0;
const MIN_PHOTON_INTERVAL_MS = 200;

async function throttlePhoton(): Promise<void> {
  const now = Date.now();
  const elapsed = now - lastPhotonCallTime;
  if (elapsed < MIN_PHOTON_INTERVAL_MS) {
    await new Promise((r) => setTimeout(r, MIN_PHOTON_INTERVAL_MS - elapsed));
  }
  lastPhotonCallTime = Date.now();
}

const NON_TOWN_REGEX =
  /\b(road|rd|street|st|avenue|ave|highway|hwy|freeway|fwy|blvd|boulevard|lane|ln|drive|dr|trail|track|way|route|rt|interstate|i-\d+|powerline|railway|culvert|overflow|crossing|quarry|junction|exit|connector|ramp|transfer station|overpass|underpass)\b/i;

const US_STATE_ABBR: Record<string, string> = {
  california: 'CA',
  nevada: 'NV',
  arizona: 'AZ',
  utah: 'UT',
  oregon: 'OR',
  washington: 'WA',
  colorado: 'CO',
  new_york: 'NY',
  illinois: 'IL',
  texas: 'TX',
  florida: 'FL',
};

function formatStateAbbr(state?: string | null): string {
  if (!state) return '';
  const clean = state.trim();
  if (clean.length === 2) return clean.toUpperCase();
  const lower = clean.toLowerCase().replace(/\s+/g, '_');
  return US_STATE_ABBR[lower] || clean;
}

/** Mile markers and raw coordinates are honest fallbacks, not a locality. */
export function isPlaceFallback(name?: string | null): boolean {
  const named = name?.trim() ?? '';
  if (!named) return true;
  if (/^mile\s+\d+/i.test(named)) return true;
  if (/^along route\b/i.test(named)) return true;
  if (/^-?\d+\.\d+,\s*-?\d+\.\d+$/.test(named)) return true;
  return false;
}

/**
 * Reverse geocode a coordinate to the nearest real town or city.
 * Throttled and cached. Town hits are cached; mile and coordinate fallbacks are not.
 */
export async function reverseGeocode(coord: LatLng, distanceM?: number): Promise<string> {
  const key = `${coord.latitude.toFixed(3)},${coord.longitude.toFixed(3)}`;
  if (reverseGeocodeCache.has(key)) {
    return reverseGeocodeCache.get(key)!;
  }

  const fallbackMile =
    distanceM !== undefined && distanceM >= 0
      ? `Mile ${Math.round(distanceM / 1609.344)}`
      : `${coord.latitude.toFixed(3)}, ${coord.longitude.toFixed(3)}`;

  try {
    await throttlePhoton();
    const url = `https://photon.komoot.io/reverse?lat=${coord.latitude}&lon=${coord.longitude}&radius=80&limit=10`;
    const res = await fetch(url);
    if (res.ok) {
      const data = await res.json();
      const features = data?.features || [];

      let foundTown: string | null = null;
      let foundState: string | null = null;

      for (const f of features) {
        const p = f.properties || {};
        const candidate = p.city || p.town || p.village || p.hamlet || p.municipality;

        if (candidate && !NON_TOWN_REGEX.test(candidate)) {
          foundTown = candidate;
          foundState = p.state;
          break;
        }

        const isLocalityType =
          ['city', 'town', 'village', 'hamlet', 'locality', 'district'].includes(p.type) ||
          ['city', 'town', 'village', 'hamlet'].includes(p.osm_value);

        if (isLocalityType && p.name && !NON_TOWN_REGEX.test(p.name)) {
          foundTown = p.name;
          foundState = p.state;
          break;
        }
      }

      if (!foundTown) {
        for (const f of features) {
          const p = f.properties || {};
          const county = typeof p.county === 'string' ? p.county.trim() : '';
          if (!county || NON_TOWN_REGEX.test(county)) continue;
          foundTown = /county$/i.test(county) ? county : `${county} County`;
          foundState = p.state;
          break;
        }
      }

      if (foundTown) {
        const st = formatStateAbbr(foundState);
        const result = st ? `${foundTown}, ${st}` : foundTown;
        reverseGeocodeCache.set(key, result);
        return result;
      }
    }
  } catch {
    // network or parse failure, try fallback
  }

  return fallbackMile;
}

